import { Component, computed, EventEmitter, inject, Input, input, model, Output, Signal, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ALERT_TYPE, FORMJ_SCHEME } from 'src/app/constants';
import { SharedService } from 'src/app/Shared/shared-service.service';
import { FormJ } from 'src/model/formJ.model';
import { PaymentSchedule } from 'src/model/paymentSchedule.model';
import { BusinessService } from 'src/services/business.service';
import { FormJService } from 'src/services/form-j.service';

@Component({
    selector: 'app-generate-formj',
    templateUrl: './generate-formj.component.html',
    styleUrls: ['./generate-formj.component.scss'],
    standalone: false
})
export class GenerateFormjComponent {
  private router = inject(Router);
  private activeModal = inject(NgbActiveModal);
  protected sharedService = inject(SharedService);
  private formJService = inject(FormJService);
  private businessService = inject(BusinessService);

  companyId = input.required<number>();
  @Input() viewCustomerId: number;
  @Input() isEPF = false;
  @Input() redirect = true;
  licenseId = model<number>();

  @Output() passEntry = new EventEmitter<FormJ>();

  formJ: FormJ = new FormJ();
  selectedMonths = 1;
  onHandAmount = 0;
  monthsCount = 1;
  amount = 0;
  // licenseId: number;
  licenseStatus: number;

  disabledSubmit = false;

  epfMonthly: number;

  schedule: WritableSignal<PaymentSchedule[]> = signal([]);
  total: Signal<number> = computed(() => this.schedule().reduce((acc, curr) => acc + curr.amount * curr.monthsCount, 0));
  totalMonths: Signal<number> = computed(() => this.schedule().reduce((acc, curr) => acc + curr.monthsCount, 0));

  ngOnInit(): void {
    if (this.licenseId() === undefined)
      this.getCustomerLatestLicense();
  }

  async getCustomerLatestLicense() {
    try {
      const license = await this.businessService.getLatestLicenseByCustomerId(this.viewCustomerId);
      this.licenseId.set(license.Id);
    } catch (err) {
      if (err.status === 404) {
        this.sharedService.openAlert(`Cannot generate Form J with expired license!`, ALERT_TYPE.DANGER);
        this.reset();
      }
    }
  }

  checkValidity(): boolean {
    if (this.amount == 0 || this.amount == undefined || this.monthsCount == 0 || this.monthsCount == undefined) {
      this.sharedService.openAlert("Please key in your amount and months count", ALERT_TYPE.DANGER);
      return false;
    } else return true;
  }

  addSchedule() {
    if (this.checkValidity()) {
      const schedule = { monthsCount: this.monthsCount, amount: this.amount }
      this.schedule.update((s) => [...s, schedule]);
      this.calculateAllAmount();
    }
  }

  removeSchedule(index: number) {
    this.schedule.update(s => s.filter((_, i) => i !== index));
    this.calculateAllAmount();
  }

  calculateAllAmount() {
    this.formJ = new FormJ();
    const interestRate = 0.015;
    const roundedPrincipal = Math.round((this.total() * 100 / ((this.totalMonths() * interestRate) + 1))) / 100;
    this.formJ.Principal = Math.floor(roundedPrincipal / 50) * 50;
    const interestCharge = this.formJ.Principal * interestRate * this.totalMonths();
    this.formJ.LastPayment = this.formJ.Principal + interestCharge - this.schedule().reduce((acc, current, i, array) => acc + (current.monthsCount - ((i < array.length - 1) ? 0 : 1)) * current.amount, 0);
  }

  checkEPFValidity(): boolean {
    if (this.selectedMonths == 0 || this.selectedMonths == undefined || this.onHandAmount == 0 || this.onHandAmount == undefined) {
      this.sharedService.openAlert("Please key in your on hand amount and months count", ALERT_TYPE.DANGER);
      return false;
    } else return true;
  }

  getEPFScheduleAndCalculate() {
    if (this.checkEPFValidity()) {
      const interestRate = 0.015;
      this.formJ = new FormJ();

      /**
       * 2024-01-18 - epf form J version 2, need to change from calculating principal based on total payment into the on hand amount
       * previous calculation:
       * this.principal = Math.ceil((this.desiredTotal / 50) / (1 + this.selectedMonths * interestRate)) * 50;
       */
      const principal = this.onHandAmount;
      this.formJ.Principal = principal;
      this.epfMonthly = Math.floor(principal * interestRate);
      this.schedule.set([{ monthsCount: this.selectedMonths, amount: this.epfMonthly }]);
      this.formJ.LastPayment = principal + (principal * interestRate * this.selectedMonths) - (this.epfMonthly * (this.selectedMonths - 1));
    }
  }

  checkGenerateValidity() {
    if (this.schedule().length == 0 || this.schedule() == undefined) {
      this.sharedService.openAlert("Please fill in your payment schedule!", ALERT_TYPE.DANGER);
      return false;
    } else return true;
  }

  async generateFormJ() {
    if (this.checkGenerateValidity()) {
      this.disabledSubmit = true;

      this.formJ.PaymentSchedule = this.schedule();
      this.formJ.Term = this.totalMonths();
      this.formJ.Version = (this.isEPF === true) ? "epfVer2" : "swastaVer1";
      this.formJ.CustomerId = this.viewCustomerId;
      this.formJ.LicenseId = this.licenseId();
      this.formJ.Scheme = (this.isEPF === true) ? FORMJ_SCHEME.EPF : FORMJ_SCHEME.SWASTA;

      try {
        this.formJ = await this.formJService.postAddFormJ(this.formJ);
        this.sharedService.openAlert("Successfully added form J record!", ALERT_TYPE.SUCCESS);
        if (this.redirect) this.goToFormJ(); else this.passEntry.emit(this.formJ);
        this.reset();
      } finally {
        this.disabledSubmit = false;
      }
    }
  }

  goToFormJ() {
    const companyId = Number(sessionStorage.getItem('companyId'));
    this.router.navigateByUrl('/formJ/' + companyId + "/" + this.viewCustomerId + "/" + this.formJ.Id);
  }

  reset() {
    this.activeModal.close();
  }
}
